import { Position } from "@xyflow/react";
/**
 * Default arc radius at corners. Matches the feel of xyflow's
 * `getSmoothStepPath` (which uses 5 by default, nudged up here for a slightly
 * softer BPMN look — the zero-waypoint branch still uses RF's own function,
 * so this only affects paths the user has bent).
 */ const DEFAULT_RADIUS = 8;
function axisFromPosition(pos) {
    return pos === Position.Left || pos === Position.Right ? "H" : "V";
}
/**
 * Build the polyline vertices for a smoothstep path through ordered waypoints.
 *
 * Each segment (point i → point i+1) is an L-shape whose type (`HV` =
 * horizontal-then-vertical, `VH` = vertical-then-horizontal) is derived from:
 *  - the FIRST segment's type: from `sourcePosition` (Left/Right → HV, Top/Bottom → VH)
 *  - MIDDLE segments propagate the same type; that rule naturally produces a
 *    right-angle bend at each waypoint (segment i's end-axis differs from
 *    segment i+1's start-axis when types match)
 *  - the LAST segment: from `targetPosition` (Left/Right → VH so last leg is
 *    horizontal into the handle; Top/Bottom → HV so last leg is vertical)
 *
 * When the last segment's prescribed type disagrees with the propagated type,
 * the waypoint-corner between them becomes collinear (no bend) — that is
 * intentional: it means the user has placed a waypoint along what would be a
 * straight segment. The path stays orthogonal.
 */ function buildPolyline(source, target, sourcePosition, targetPosition, waypoints) {
    const pts = [
        source,
        ...waypoints,
        target
    ];
    const sourceAxis = axisFromPosition(sourcePosition);
    const targetAxis = axisFromPosition(targetPosition);
    const segCount = pts.length - 1;
    // First segment type: HV if source exits horizontally else VH.
    const firstType = sourceAxis === "H" ? "HV" : "VH";
    // Last segment type: last leg must match target's axis, so for H-entry we
    // need VH (second leg horizontal) and for V-entry we need HV.
    const lastType = targetAxis === "H" ? "VH" : "HV";
    const segTypes = [];
    for(let i = 0; i < segCount; i++){
        if (i === 0) segTypes.push(firstType);
        else if (i === segCount - 1) segTypes.push(lastType);
        else segTypes.push(firstType); // middle segments propagate first type
    }
    const polyline = [
        pts[0]
    ];
    for(let i = 0; i < segCount; i++){
        const a = pts[i];
        const b = pts[i + 1];
        const t = segTypes[i];
        const corner = t === "HV" ? {
            x: b.x,
            y: a.y
        } : {
            x: a.x,
            y: b.y
        };
        // Skip degenerate corners (zero-length leg).
        if (!(corner.x === a.x && corner.y === a.y) && !(corner.x === b.x && corner.y === b.y)) {
            polyline.push(corner);
        }
        polyline.push(b);
    }
    // Collapse consecutive duplicates.
    const deduped = [];
    for (const p of polyline){
        const last = deduped[deduped.length - 1];
        if (!last || last.x !== p.x || last.y !== p.y) deduped.push(p);
    }
    return deduped;
}
function legAxis(a, b) {
    if (a.x === b.x && a.y === b.y) return null;
    if (a.x === b.x) return "V";
    if (a.y === b.y) return "H";
    // Non-orthogonal shouldn't happen with our polyline builder; treat as H.
    return "H";
}
/**
 * Convert a polyline into an SVG path `d` string with rounded corners at every
 * right-angle bend. Collinear vertices are passed through as straight line
 * segments. Radius is clamped to half the shorter adjacent leg so it never
 * eats past the corner.
 */ function polylineToPathD(polyline, radius) {
    if (polyline.length === 0) return "";
    if (polyline.length === 1) return `M ${polyline[0].x},${polyline[0].y}`;
    let d = `M ${polyline[0].x},${polyline[0].y}`;
    for(let i = 1; i < polyline.length - 1; i++){
        const prev = polyline[i - 1];
        const curr = polyline[i];
        const next = polyline[i + 1];
        const aIn = legAxis(prev, curr);
        const aOut = legAxis(curr, next);
        if (aIn === null || aOut === null || aIn === aOut) {
            // Collinear (or degenerate) — just line to curr.
            d += ` L ${curr.x},${curr.y}`;
            continue;
        }
        const lenIn = Math.hypot(curr.x - prev.x, curr.y - prev.y);
        const lenOut = Math.hypot(next.x - curr.x, next.y - curr.y);
        const r = Math.max(0, Math.min(radius, lenIn / 2, lenOut / 2));
        const signInX = Math.sign(curr.x - prev.x);
        const signInY = Math.sign(curr.y - prev.y);
        const signOutX = Math.sign(next.x - curr.x);
        const signOutY = Math.sign(next.y - curr.y);
        const entryX = curr.x - signInX * r;
        const entryY = curr.y - signInY * r;
        const exitX = curr.x + signOutX * r;
        const exitY = curr.y + signOutY * r;
        d += ` L ${entryX},${entryY}`;
        if (r > 0) {
            // Quadratic through the corner — visually indistinguishable from a
            // quarter-circle arc at this radius and keeps the path string terse.
            d += ` Q ${curr.x},${curr.y} ${exitX},${exitY}`;
        }
    }
    const last = polyline[polyline.length - 1];
    d += ` L ${last.x},${last.y}`;
    return d;
}
function polylineLength(polyline) {
    let total = 0;
    for(let i = 1; i < polyline.length; i++){
        total += Math.hypot(polyline[i].x - polyline[i - 1].x, polyline[i].y - polyline[i - 1].y);
    }
    return total;
}
/**
 * Arc-length midpoint of a polyline, plus the direction of the segment it lands
 * on. The tangent lets callers offset a label perpendicular to the LOCAL leg
 * (rather than the source→target chord), so labels stay adjacent to the edge
 * even where it bends.
 */ export function polylineMidpointWithTangent(polyline) {
    const total = polylineLength(polyline);
    if (total === 0) {
        const p = polyline[0] ?? {
            x: 0,
            y: 0
        };
        return {
            x: p.x,
            y: p.y,
            tangentX: 1,
            tangentY: 0
        };
    }
    const target = total / 2;
    let acc = 0;
    for(let i = 1; i < polyline.length; i++){
        const a = polyline[i - 1];
        const b = polyline[i];
        const len = Math.hypot(b.x - a.x, b.y - a.y);
        if (acc + len >= target) {
            const t = (target - acc) / (len || 1);
            return {
                x: a.x + (b.x - a.x) * t,
                y: a.y + (b.y - a.y) * t,
                tangentX: b.x - a.x,
                tangentY: b.y - a.y
            };
        }
        acc += len;
    }
    const a = polyline[polyline.length - 2];
    const last = polyline[polyline.length - 1];
    return {
        x: last.x,
        y: last.y,
        tangentX: last.x - a.x,
        tangentY: last.y - a.y
    };
}
function polylineMidpoint(polyline) {
    const { x, y } = polylineMidpointWithTangent(polyline);
    return {
        x,
        y
    };
}
/**
 * Flatten a chain of contiguous line segments (e.g. `pathToSegments` output)
 * into polyline vertices. Assumes each segment begins where the previous one
 * ended, which holds for a single-subpath edge `d` string.
 */ export function segmentsToPolyline(segments) {
    if (segments.length === 0) return [];
    const vertices = [
        {
            x: segments[0].x1,
            y: segments[0].y1
        }
    ];
    for (const s of segments)vertices.push({
        x: s.x2,
        y: s.y2
    });
    return vertices;
}
/**
 * Anchor point + local tangent for an edge label, derived from the edge's
 * already-parsed path segments. Anchoring to the rendered stroke (rather than
 * reconstructing the geometry or interpolating the source→target chord) is what
 * keeps the label on the line through handle-exit offsets, backward-edge
 * detours, and waypoint bends. Falls back to the straight chord when the path
 * parsed to fewer than two vertices. Callers pass the segments `useBridgeEdge`
 * already computed, so the path is parsed once per edge, not twice.
 */ export function labelAnchorFromSegments(segments, fallbackChord) {
    const vertices = segmentsToPolyline(segments);
    const polyline = vertices.length >= 2 ? vertices : [
        {
            x: fallbackChord.sourceX,
            y: fallbackChord.sourceY
        },
        {
            x: fallbackChord.targetX,
            y: fallbackChord.targetY
        }
    ];
    return polylineMidpointWithTangent(polyline);
}
/**
 * Build an orthogonal smoothstep path through the given waypoints. Returns
 * `[pathD, labelX, labelY]`, matching the shape of xyflow's `getSmoothStepPath`
 * so FlowEdge can dispatch transparently.
 *
 * Callers must handle the zero-waypoint case themselves (use RF's
 * `getSmoothStepPath` for the fast path / regression-free rendering). This
 * builder always produces a polyline from source through at least one
 * waypoint to target.
 */ export function buildSmoothStepPathThroughWaypoints(params) {
    const { sourceX, sourceY, sourcePosition, targetX, targetY, targetPosition, waypoints, radius = DEFAULT_RADIUS } = params;
    const polyline = buildPolyline({
        x: sourceX,
        y: sourceY
    }, {
        x: targetX,
        y: targetY
    }, sourcePosition, targetPosition, waypoints);
    const d = polylineToPathD(polyline, radius);
    const mid = polylineMidpoint(polyline);
    return [
        d,
        mid.x,
        mid.y
    ];
}
/**
 * Compute the orthogonal polyline vertices for an edge. Returns the same
 * geometry that `buildSmoothStepPathThroughWaypoints` renders through for the
 * ≥1-waypoint case, and an RF-equivalent HVH (or VHV) polyline for the
 * zero-waypoint case.
 *
 * The result is used by ghost-midpoint computation so dashed ghost dots sit on
 * the rendered stroke rather than floating on chord midpoints between active
 * points.
 */ export function computeOrthogonalPolyline(params) {
    const { sourceX, sourceY, sourcePosition, targetX, targetY, targetPosition, waypoints, centerX } = params;
    const source = {
        x: sourceX,
        y: sourceY
    };
    const target = {
        x: targetX,
        y: targetY
    };
    if (waypoints.length > 0) {
        return buildPolyline(source, target, sourcePosition, targetPosition, waypoints);
    }
    const sourceAxis = axisFromPosition(sourcePosition);
    const raw = [
        source
    ];
    if (sourceAxis === "H") {
        const cx = centerX ?? (sourceX + targetX) / 2;
        raw.push({
            x: cx,
            y: sourceY
        });
        raw.push({
            x: cx,
            y: targetY
        });
    } else {
        const cy = (sourceY + targetY) / 2;
        raw.push({
            x: sourceX,
            y: cy
        });
        raw.push({
            x: targetX,
            y: cy
        });
    }
    raw.push(target);
    const deduped = [];
    for (const p of raw){
        const last = deduped[deduped.length - 1];
        if (!last || last.x !== p.x || last.y !== p.y) deduped.push(p);
    }
    return deduped;
}
/**
 * Emit one ghost per rendered leg of the orthogonal polyline, tagged with the
 * `insertAtIndex` that places the promoted waypoint in the correct slot of the
 * waypoint array.
 *
 * An "active pair" is a consecutive pair of active points (source+waypoints+
 * target). A pair contributes 1 or 2 legs to the polyline (2 when there's a
 * corner between the active points, 1 when they're collinear). Every leg
 * belonging to pair `k` inserts at index `k` — both the "pre-corner" and the
 * "post-corner" legs of pair 0 land a new waypoint at the head of the array,
 * etc.
 */ export function computeGhostMidpoints(params) {
    const { source, target, sourcePosition, targetPosition, waypoints, centerX } = params;
    const polyline = computeOrthogonalPolyline({
        sourceX: source.x,
        sourceY: source.y,
        sourcePosition,
        targetX: target.x,
        targetY: target.y,
        targetPosition,
        waypoints,
        centerX
    });
    const mids = [];
    let wIdx = 0;
    let pairIdx = 0;
    for(let i = 0; i < polyline.length - 1; i++){
        const v = polyline[i];
        // Crossing a waypoint vertex transitions us to the next active-pair.
        if (wIdx < waypoints.length && v.x === waypoints[wIdx].x && v.y === waypoints[wIdx].y) {
            wIdx++;
            pairIdx++;
        }
        const next = polyline[i + 1];
        if (v.x === next.x && v.y === next.y) continue;
        mids.push({
            id: `ghost:pair-${pairIdx}:leg-${i}`,
            x: (v.x + next.x) / 2,
            y: (v.y + next.y) / 2,
            insertAtIndex: pairIdx
        });
    }
    return mids;
}

//# sourceMappingURL=smoothStepPath.js.map